'use client'

import { useTranslation } from '@/hooks/client/use-translation'

import type { PriceLevelTab } from './price-section-types'
import type { UpgradeComparison } from './upgrade-comparison'

import { NativeProductSelect } from '../native-product-select'

interface PriceFeatureBaselineProps {
  levels: PriceLevelTab[]
  comparisons: UpgradeComparison[]
  selectedId: number
  onSelect: (levelId: number) => void
}

export const PriceFeatureBaseline = ({ levels, comparisons, selectedId, onSelect }: PriceFeatureBaselineProps) => {
  const { t } = useTranslation()
  const options = comparisons.map((comparison) => {
    const level = levels.find((entry) => entry.id === comparison.levelId)
    return {
      id: comparison.levelId,
      name: level ? `${level.name} ${level.levelName}` : comparison.levelName,
    }
  })
  const selected = options.find((option) => option.id === selectedId) ?? options[0]
  if (!selected) {
    return null
  }
  if (options.length === 1) {
    return (
      <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-navy-60">
        <span>{t('product.panel.comparingWith', 'Comparing with')}</span>
        <span className="font-semibold text-navy">{selected.name}</span>
      </p>
    )
  }
  const label = t('product.panel.compareWith', 'Compare with')
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
      <span className="text-navy-60">{label}</span>
      <NativeProductSelect
        value={String(selected.id)}
        onValueChange={(value) => onSelect(Number(value))}
        options={options.map((option) => (
          <option key={option.id} value={String(option.id)}>
            {option.name}
          </option>
        ))}
        ariaLabel={t('product.upgradeChangeComparison', 'Change comparison from {{name}}', { name: selected.name })}
        className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-sm font-semibold text-navy hover:text-lapis focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis"
      >
        <span className="underline decoration-2 underline-offset-4">{selected.name}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="m3 6 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </NativeProductSelect>
    </div>
  )
}
